import { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { ROLES, isActive } from "../lib/auth";
import { statusValue, todayStr } from "../lib/progress";
import { STUDENT_TYPES, STUDENT_TYPE_STYLES } from "../lib/studentTypes";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    CalendarRange,
    TrendingUp,
    CircleX,
    Percent,
    Users,
} from "lucide-react";

function monthStartStr() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Given an inclusive [start, end] range as YYYY-MM-DD strings, return the
 * prior period of equal length: [priorStart, priorEnd] where priorEnd is
 * the day before `start`.
 */
function priorPeriod(start, end) {
    if (!start || !end) return { priorStart: "", priorEnd: "" };
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    const days = Math.round((e - s) / 86400000) + 1;
    const priorEndDate = new Date(s.getTime() - 86400000);
    const priorStartDate = new Date(priorEndDate.getTime() - (days - 1) * 86400000);
    const toIso = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { priorStart: toIso(priorStartDate), priorEnd: toIso(priorEndDate) };
}

/**
 * Reports — finance officer view. Focuses on **lessons made** (billable units)
 * broken down by student residency type (Local / Regional / International —
 * different rates each) and by teacher.
 *
 * `Lessons made` uses statusValue():
 *   Present = 1, Double Session = 2,
 *   Excused with a "Done on …" remark = 1 (make-up delivered),
 *   Absent / unresolved Excused = 0.
 *
 * Excused and Double Session are intentionally not surfaced as separate
 * summary cards — finance only cares about the sum of billable lessons.
 */
export default function Reports() {
    const { students, attendance } = useData();
    const { isAdmin, teachers } = useAuth();
    const [start, setStart] = useState(monthStartStr());
    const [end, setEnd] = useState(todayStr());
    const [teacherFilter, setTeacherFilter] = useState("all");

    const teacherFilteredAttendance = useMemo(() => {
        if (!isAdmin || teacherFilter === "all") return attendance;
        return attendance.filter((r) => r.teacherId === teacherFilter);
    }, [attendance, isAdmin, teacherFilter]);

    const teacherFilteredStudents = useMemo(() => {
        if (!isAdmin || teacherFilter === "all") return students;
        return students.filter((s) => s.teacherId === teacherFilter);
    }, [students, isAdmin, teacherFilter]);

    const inRange = useMemo(() => {
        return teacherFilteredAttendance.filter((r) => {
            if (start && r.date < start) return false;
            if (end && r.date > end) return false;
            return true;
        });
    }, [teacherFilteredAttendance, start, end]);

    // Prior period totals — used for the Attendance Rate delta chip.
    const attendanceRateDelta = useMemo(() => {
        const { priorStart, priorEnd } = priorPeriod(start, end);
        if (!priorStart || !priorEnd) return null;
        let priorPresent = 0;
        let priorAbsent = 0;
        for (const r of teacherFilteredAttendance) {
            if (r.date < priorStart || r.date > priorEnd) continue;
            if (r.status === "Present") priorPresent += 1;
            if (r.status === "Absent") priorAbsent += 1;
        }
        if (priorPresent + priorAbsent === 0) return null;
        return Math.round((priorPresent / (priorPresent + priorAbsent)) * 100);
    }, [teacherFilteredAttendance, start, end]);

    // Map studentId -> type so we can bucket lessons-made by residency.
    const typeByStudent = useMemo(() => {
        const m = {};
        for (const s of teacherFilteredStudents) m[s.id] = s.type;
        return m;
    }, [teacherFilteredStudents]);

    // Lessons made bucketed by student type.
    const totals = useMemo(() => {
        const byType = { Local: 0, Regional: 0, International: 0 };
        let totalLessonsMade = 0;
        let present = 0;
        let absent = 0;
        for (const r of inRange) {
            const units = statusValue(r);
            totalLessonsMade += units;
            const t = typeByStudent[r.studentId] || "Local";
            if (byType[t] !== undefined) byType[t] += units;
            if (r.status === "Present") present += 1;
            if (r.status === "Absent") absent += 1;
        }
        return { totalLessonsMade, byType, present, absent };
    }, [inRange, typeByStudent]);

    const perStudent = useMemo(() => {
        const rows = teacherFilteredStudents
            .map((s) => {
                const recs = inRange.filter((r) => r.studentId === s.id);
                let lessonsMade = 0;
                let present = 0;
                let absent = 0;
                for (const r of recs) {
                    lessonsMade += statusValue(r);
                    if (r.status === "Present") present += 1;
                    if (r.status === "Absent") absent += 1;
                }
                return {
                    id: s.id,
                    name: s.name,
                    instrument: s.instrument,
                    type: s.type,
                    teacherId: s.teacherId,
                    total: recs.length,
                    present,
                    absent,
                    lessonsMade,
                };
            })
            .filter((r) => r.total > 0)
            .sort((a, b) => b.lessonsMade - a.lessonsMade);
        return rows;
    }, [teacherFilteredStudents, inRange]);

    const teacherUsers = teachers.filter(
        (t) => t.role === ROLES.TEACHER && isActive(t),
    );

    const perTeacher = useMemo(() => {
        if (!isAdmin) return [];
        return teacherUsers
            .map((t) => {
                const recs = inRange.filter((r) => r.teacherId === t.id);
                const byType = { Local: 0, Regional: 0, International: 0 };
                let lessonsMade = 0;
                for (const r of recs) {
                    const units = statusValue(r);
                    lessonsMade += units;
                    const type = typeByStudent[r.studentId] || "Local";
                    if (byType[type] !== undefined) byType[type] += units;
                }
                const studentSet = new Set(
                    teacherFilteredStudents
                        .filter((s) => s.teacherId === t.id)
                        .map((s) => s.id),
                );
                return {
                    id: t.id,
                    name: t.name,
                    students: studentSet.size,
                    total: recs.length,
                    byType,
                    lessonsMade,
                };
            })
            .filter(
                (r) => r.total > 0 || teacherFilter === "all" || teacherFilter === r.id,
            );
    }, [isAdmin, teacherUsers, inRange, teacherFilteredStudents, teacherFilter, typeByStudent]);

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">
                    Finance
                </div>
                <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                    Reports
                </h1>
                <p className="text-sm text-ink-mute mt-2 max-w-2xl">
                    Lessons made in the selected range, broken down by student
                    residency type. Present = 1 · Double Session = 2 · Resolved
                    Excused (make-up done) = 1 · Absent = 0.
                </p>
            </div>

            {/* Filter */}
            <div className="bg-white rounded-3xl border border-mist card-soft p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-mist text-slate-600 flex items-center justify-center">
                        <CalendarRange size={18} strokeWidth={2.5} />
                    </div>
                    <h2 className="font-display font-bold text-lg uppercase tracking-tight">
                        Date Range
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                            Start
                        </Label>
                        <Input
                            type="date"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            className="h-11 rounded-xl bg-mist/50 border-mist"
                            data-testid="reports-start"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                            End
                        </Label>
                        <Input
                            type="date"
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            className="h-11 rounded-xl bg-mist/50 border-mist"
                            data-testid="reports-end"
                        />
                    </div>
                </div>
                {isAdmin && (
                    <div className="mt-5 pt-5 border-t border-mist">
                        <div className="flex items-center gap-2 mb-3">
                            <Users size={14} className="text-slate-400" strokeWidth={2.5} />
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Teacher
                            </Label>
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="report-teacher-filter">
                            <button
                                onClick={() => setTeacherFilter("all")}
                                data-testid="report-teacher-all"
                                aria-pressed={teacherFilter === "all"}
                                className={`focus-ring h-10 px-4 rounded-full text-sm font-bold border-2 transition-all ${
                                    teacherFilter === "all"
                                        ? "bg-ink text-white border-slate-900"
                                        : "bg-mist/50 text-slate-600 border-mist hover:bg-mist"
                                }`}
                            >
                                All teachers
                            </button>
                            {teacherUsers.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setTeacherFilter(t.id)}
                                    data-testid={`report-teacher-${t.id}`}
                                    aria-pressed={teacherFilter === t.id}
                                    className={`focus-ring h-10 px-4 rounded-full text-sm font-bold border-2 transition-all ${
                                        teacherFilter === t.id
                                            ? "bg-[#3D6385] text-white border-[#3D6385]"
                                            : "bg-mist/50 text-slate-600 border-mist hover:bg-mist"
                                    }`}
                                >
                                    {t.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Summary cards — lessons made by type + Absent + Attendance Rate */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
                <SummaryCard
                    label="Total Lessons Made"
                    value={totals.totalLessonsMade}
                    icon={TrendingUp}
                    color="text-[#3D6385]"
                    bg="bg-action-soft"
                    testId="sum-total"
                    emphasize
                />
                {STUDENT_TYPES.map((t) => {
                    const style = STUDENT_TYPE_STYLES[t];
                    return (
                        <SummaryCard
                            key={t}
                            label={`${t} · Lessons`}
                            value={totals.byType[t]}
                            icon={style.icon}
                            color={style.fg}
                            bg={style.bg}
                            testId={`sum-lessons-${t.toLowerCase()}`}
                        />
                    );
                })}
                <SummaryCard
                    label="Absent"
                    value={totals.absent}
                    icon={CircleX}
                    color="text-danger"
                    bg="bg-danger-soft"
                    testId="sum-absent"
                />
                <SummaryCard
                    label="Attendance Rate"
                    value={
                        totals.present + totals.absent > 0
                            ? `${Math.round(
                                  (totals.present /
                                      (totals.present + totals.absent)) *
                                      100,
                              )}%`
                            : "—"
                    }
                    icon={Percent}
                    color="text-success"
                    bg="bg-success-soft"
                    testId="sum-attendance-rate"
                    sub={(() => {
                        if (
                            totals.present + totals.absent === 0 ||
                            attendanceRateDelta === null
                        ) {
                            return null;
                        }
                        const currentRate = Math.round(
                            (totals.present / (totals.present + totals.absent)) *
                                100,
                        );
                        const diff = currentRate - attendanceRateDelta;
                        if (diff === 0) {
                            return { text: "same vs prior", tone: "neutral" };
                        }
                        return {
                            text: `${diff > 0 ? "+" : "−"}${Math.abs(diff)} pts vs prior`,
                            tone: diff > 0 ? "up" : "down",
                        };
                    })()}
                />
            </div>

            {/* Per-teacher table (admin only) */}
            {isAdmin && perTeacher.length > 0 && (
                <div className="bg-white rounded-3xl border border-mist card-soft overflow-hidden">
                    <div className="p-6 sm:p-8 border-b border-mist">
                        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                            Per-Teacher Totals
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Lessons made per teacher, split by student residency type.
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table
                            className="min-w-full"
                            data-testid="reports-teacher-table"
                        >
                            <thead>
                                <tr className="bg-mist/50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                                    <th className="text-left px-6 py-3">Teacher</th>
                                    <th className="text-center px-4 py-3">Students</th>
                                    <th className="text-center px-4 py-3">Local</th>
                                    <th className="text-center px-4 py-3">Regional</th>
                                    <th className="text-center px-4 py-3">Intl</th>
                                    <th className="text-right px-6 py-3">Total Lessons</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-mist">
                                {perTeacher.map((row) => (
                                    <tr
                                        key={row.id}
                                        data-testid={`report-teacher-row-${row.id}`}
                                    >
                                        <td className="px-6 py-4 font-bold text-slate-900">
                                            {row.name}
                                        </td>
                                        <td className="px-4 py-4 text-center font-semibold text-slate-700">
                                            {row.students}
                                        </td>
                                        <td className="px-4 py-4 text-center font-semibold text-success">
                                            {row.byType.Local}
                                        </td>
                                        <td className="px-4 py-4 text-center font-semibold text-warning">
                                            {row.byType.Regional}
                                        </td>
                                        <td className="px-4 py-4 text-center font-semibold text-brand">
                                            {row.byType.International}
                                        </td>
                                        <td className="px-6 py-4 text-right font-display font-bold text-lg text-slate-900">
                                            {row.lessonsMade}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Per-student table */}
            <div className="bg-white rounded-3xl border border-mist card-soft overflow-hidden">
                <div className="p-6 sm:p-8 border-b border-mist">
                    <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                        Per-Student Totals
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Sorted by lessons made · Present = 1 · Double = 2 · Resolved
                        Excused = 1.
                    </p>
                </div>

                {perStudent.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                        No attendance records in this range.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full" data-testid="reports-table">
                            <thead>
                                <tr className="bg-mist/50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                                    <th className="text-left px-6 py-3">Student</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Instrument</th>
                                    <th className="text-center px-4 py-3">Present</th>
                                    <th className="text-center px-4 py-3">Absent</th>
                                    <th className="text-right px-6 py-3">Lessons Made</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-mist">
                                {perStudent.map((row) => {
                                    const style = STUDENT_TYPE_STYLES[row.type] || STUDENT_TYPE_STYLES.Local;
                                    return (
                                        <tr key={row.id} data-testid={`report-row-${row.id}`}>
                                            <td className="px-6 py-4 font-bold text-slate-900">
                                                {row.name}
                                            </td>
                                            <td className="px-4 py-4">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${style.chip}`}
                                                >
                                                    {row.type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-slate-600 text-sm">
                                                {row.instrument}
                                            </td>
                                            <td className="px-4 py-4 text-center font-semibold text-success">
                                                {row.present}
                                            </td>
                                            <td className="px-4 py-4 text-center font-semibold text-danger">
                                                {row.absent}
                                            </td>
                                            <td className="px-6 py-4 text-right font-display font-bold text-lg text-slate-900">
                                                {row.lessonsMade}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function SummaryCard({ label, value, icon: Icon, color, bg, testId, emphasize = false, sub = null }) {
    const toneClasses = {
        up: "bg-success-soft text-success",
        down: "bg-danger-soft text-danger",
        neutral: "bg-mist text-ink-mute",
    };
    return (
        <div
            className={`bg-white rounded-3xl border card-soft p-5 ${
                emphasize ? "border-action/30 ring-1 ring-action/20" : "border-mist"
            }`}
            data-testid={testId}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    {label}
                </span>
                <div
                    className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}
                >
                    <Icon size={16} className={color} strokeWidth={2.6} />
                </div>
            </div>
            <div className="font-display font-bold text-3xl sm:text-4xl leading-none text-slate-900">
                {value}
            </div>
            {sub && (
                <div
                    className={`mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${toneClasses[sub.tone] || toneClasses.neutral}`}
                    data-testid={`${testId}-delta`}
                >
                    {sub.text}
                </div>
            )}
        </div>
    );
}
